// src/components/programs/ProgramForm/helpers/formConstants.js

/**
 * Initial form data state
 */
export const INITIAL_FORM_DATA = {
    // Basic info
    title: { ar: '', en: '' },
    description: { ar: '', en: '' },
    agent_id: '',
    city_ids: [],
    start_date: '',
    end_date: '',
    durations_days: 0,
    durations_nights: 0,
    show_date_in_template: true,
    cover_image: null,
    cover_image_preview: '',

    // Pax types
    pax_types: [],

    // Hotels (separate step - Step 1)
    hotels: [],

    // Itinerary
    itinerary: [],

    // Global discount
    global_discount: {
        type: 'zero',
        value: null,
        target: 'none',
    },

    // Tax
    tax_input_type: 'none',
    tax_percentage: 0,
    tax_amount: 0,

    // Upload states
    uploadingCoverImage: false,
    coverImageError: '',
    coverImageSuccess: '',
};

/**
 * Empty hotel stay template (used in Step 1 Hotels)
 */
export const EMPTY_HOTEL_TEMPLATE = {
    id: null,
    check_in_date: '',
    check_out_date: '',
    nights: 0,
    rooms: 1,
    hotel_id: null,
    hotel_name: '',
    hotel_confirmation_number: '',
    country_id: null,
    city_id: null,
    check_in_time: '14:00',
    check_out_time: '12:00',
    meal_plan_id: null,
    hotel_rating_id: null,
    room_category_id: null,
    product_snapshot: null,
    selected_supplier: null,
    pricing: {
        is_price_edited: false,
        custom_pax_prices: {},
        custom_pax_costs: {},
    },
    // NEW fields for room_night_daily
    room_stays: [],
    extras: [],
};

/**
 * Empty product structure template
 */
export const EMPTY_PRODUCT_TEMPLATE = {
    instance_id: null,
    product_snapshot: {
        product_id: null,
        title: { ar: '', en: '' },
        category: null,
        pricing_unit: 'per_person',
        pricing_code: 'per_person',
        original_price: 0,
        original_cost_price: 0,
        // ✅ إضافة original_sell_price
        original_sell_price: 0,
    },
    selected_supplier: null,
    selected_service: null,
    order: 1,
    pricing: {
        price: 0,
        cost_price: 0,
        custom_price: null,
        is_price_edited: false,
        discount_type: 'zero',
        discount_value: null,
        // ✅ إضافة original_sell_price في pricing أيضاً
        original_sell_price: 0,
        custom_pax_prices: {},
        custom_pax_costs: {},   // ✅ NEW: per-pax custom cost overrides
    },
    dynamic_fields: {
        pickup_time: null,
        city_id: null,
        time: null,
        units: null,
        nights: null,
        hotel_details: {
            hotel_name: '',
            hotel_confirmation_number: '',
            country_id: null,
            city_id: null,
            check_in_time: '',
            check_out_time: '',
            meal_plan_id: null,
            hotel_rating_id: null,
            room_category_id: null,
            extras: [],
        },
    },
    calculated: {
        final_price_per_unit: 0,
        final_cost_per_unit: 0,
        quantity: 0,
        total_price: 0,
        total_cost: 0,
    },
};

/**
 * Form steps configuration
 */
export const FORM_STEPS = [
    { id: 0, key: 'info' },
    { id: 1, key: 'hotel' },
    { id: 2, key: 'data' },
    { id: 3, key: 'quotation' },
];

/**
 * Discount type options
 */
export const DISCOUNT_TYPES = {
    ZERO: 'zero',
    AMOUNT: 'amount',
    PERCENT: 'percent',
};

/**
 * Discount target options
 */
export const DISCOUNT_TARGETS = {
    NONE: 'none',
    SELL: 'sell',        // ✅ تطابق مع الباك إند
    COST: 'cost',        // ✅ تطابق مع الباك إند
    BOTH: 'both',        // ✅ تطابق مع الباك إند
};

/**
 * Pricing unit codes
 */
export const PRICING_UNIT_CODES = {
    PER_PERSON: 'per_person',
    PER_NIGHT: 'per_night',
    PER_UNIT: 'per_unit',
    PER_PERSON_NIGHT: 'per_person_night',
    ROOM_NIGHT: 'room_night',
};

/**
 * Pricing modes for hotels
 */
export const HOTEL_PRICING_MODES = {
    ROOM_NIGHT_DAILY: 'room_night_daily',
    LEGACY_PER_PERSON_NIGHT: 'legacy_per_person_night',
};

/**
 * Pricing unit ID to code mapping
 */
export const PRICING_UNIT_ID_MAP = {
    1: 'per_person',
    2: 'per_night',
    3: 'per_unit',
    4: 'per_person_night',
};

/**
 * Product type codes used in the system
 */
export const PRODUCT_TYPE_CODES = {
    FLIGHT: 'flight',
    HOTEL: 'hotel',
    TOUR: 'tour',
    TRANSFER: 'transfer',
    FREE_DAY: 'free_day',
    FREE_TIME: 'free_time',
    ARRIVAL_DESTINATION: 'arrival_destination',
    DEPARTURE_DESTINATION: 'departure_destination',
    TRANSFER_BETWEEN_HOTELS: 'transfer_between_hotels',
    HOTEL_CHECK_IN: 'hotel_check_in',
    HOTEL_CHECK_OUT: 'hotel_check_out',
    VISA: 'visa',
};

export const PRODUCT_TYPE_LABELS = {
    [PRODUCT_TYPE_CODES.TOUR]: { ar: 'رحلات', en: 'Tour' },
    [PRODUCT_TYPE_CODES.TRANSFER]: { ar: 'انتقالات', en: 'Transfer' },
    [PRODUCT_TYPE_CODES.HOTEL]: { ar: 'فنادق', en: 'Hotel' },
    [PRODUCT_TYPE_CODES.FLIGHT]: { ar: 'طيران', en: 'Flight' },
    [PRODUCT_TYPE_CODES.FREE_DAY]: { ar: 'يوم حر بدون فاعليات', en: 'Free Day without activities' },
    [PRODUCT_TYPE_CODES.FREE_TIME]: { ar: 'وقت حر بدون فاعليات', en: 'Free Time without activities' },
    [PRODUCT_TYPE_CODES.ARRIVAL_DESTINATION]: { ar: 'الوصول إلى وجهتك', en: 'Arrival in your Destination' },
    [PRODUCT_TYPE_CODES.DEPARTURE_DESTINATION]: { ar: 'المغادرة من وجهتك', en: 'Departure from your Destination' },
    [PRODUCT_TYPE_CODES.TRANSFER_BETWEEN_HOTELS]: { ar: 'الانتقال بين الفنادق', en: 'Transfer between Hotels' },
    [PRODUCT_TYPE_CODES.HOTEL_CHECK_IN]: { ar: 'تسجيل الوصول في فندقك', en: 'Check in at your Hotel' },
    [PRODUCT_TYPE_CODES.HOTEL_CHECK_OUT]: { ar: 'تسجيل المغادرة من فندقك', en: 'Check out from your Hotel' },
    [PRODUCT_TYPE_CODES.VISA]: { ar: 'فيزا', en: 'Visa' },
};

export const PRICING_CODES = {
    PER_PERSON: 'per_person',
    PER_PERSON_NIGHT: 'per_person_night',
    PER_NIGHT: 'per_night',
    PER_UNIT: 'per_unit',
    ROOM_NIGHT: 'room_night',
    FREE_DAY: 'free_day',
    FREE_TIME: 'free_time',
    PROGRAM_MARKER: 'program_marker',
    VISA: 'visa',
};

export const PRICING_CODE_LABELS = {
    [PRICING_CODES.PER_PERSON]: { ar: 'لكل فرد', en: 'Per Person' },
    [PRICING_CODES.PER_PERSON_NIGHT]: { ar: 'لكل فرد / ليلة', en: 'Per Person / Night' },
    [PRICING_CODES.PER_NIGHT]: { ar: 'لكل ليلة', en: 'Per Night' },
    [PRICING_CODES.PER_UNIT]: { ar: 'لكل وحدة', en: 'Per Unit' },
    [PRICING_CODES.ROOM_NIGHT]: { ar: 'غرفة / ليلة', en: 'Room / Night' },
    [PRICING_CODES.FREE_DAY]: { ar: 'يوم حر', en: 'Free Day' },
    [PRICING_CODES.FREE_TIME]: { ar: 'وقت حر', en: 'Free Time' },
    [PRICING_CODES.PROGRAM_MARKER]: { ar: 'علامة في خط السير', en: 'Itinerary Marker' },
    [PRICING_CODES.VISA]: { ar: 'فيزا', en: 'Visa' },
};
